import React from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import type { StackNavigationProp } from '@react-navigation/stack';
import type { RootStackParamList } from '../types/navigation';
import { Icon } from '../components/Icon';
import { colors, spacing, fontWeights, borderRadius } from '../theme';

type NavigationProp = StackNavigationProp<RootStackParamList, 'M08CompareScreen'>;

export const M08CompareScreen: React.FC = () => {
  const navigation = useNavigation<NavigationProp>();

  const handleSelectOption = () => {
    // Navigate back to ResultScreen
    navigation.navigate('M07ResultScreen');
  };

  return (
    <View style={styles.container}>
      <ScrollView contentContainerStyle={styles.scrollContent}>
        <View style={styles.header}>
          <Text style={styles.headerTitle}>So sánh các phương án đóng phí</Text>
          <Text style={styles.headerSub}>Khách hàng: Nguyễn Văn Minh - 35t Nam</Text>
          <Text style={styles.headerSub}>Sản phẩm: ManulifeMOVE • STBH: 1 tỷ VNĐ</Text>
        </View>

        {/* Bảng so sánh */}
        <View style={styles.card}>
          <Text style={styles.cardTitle}>📋 Bảng so sánh các mốc thời hạn</Text>
          <View style={styles.table}>
            {/* Headers */}
            <View style={styles.tableHeader}>
              <Text style={[styles.tableCell, styles.headerCell, { flex: 1.2, textAlign: 'left', paddingLeft: 8 }]}>Chỉ số</Text>
              <Text style={[styles.tableCell, styles.headerCell]}>10 năm</Text>
              <Text style={[styles.tableCell, styles.headerCell]}>15 năm</Text>
              <Text style={[styles.tableCell, styles.headerCell]}>20 năm</Text>
            </View>

            {/* Row 1 */}
            <View style={styles.tableRow}>
              <Text style={[styles.tableCell, styles.labelCell, { flex: 1.2 }]}>Phí đóng/năm</Text>
              <Text style={styles.tableCell}>50M</Text>
              <Text style={styles.tableCell}>38M</Text>
              <Text style={styles.tableCell}>30M</Text>
            </View>

            {/* Row 2 */}
            <View style={styles.tableRow}>
              <Text style={[styles.tableCell, styles.labelCell, { flex: 1.2 }]}>Tổng phí đóng</Text>
              <Text style={styles.tableCell}>500M</Text>
              <Text style={styles.tableCell}>570M</Text>
              <Text style={styles.tableCell}>600M</Text>
            </View>

            {/* Row 3 */}
            <View style={styles.tableRow}>
              <Text style={[styles.tableCell, styles.labelCell, { flex: 1.2 }]}>Mệnh giá bảo vệ</Text>
              <Text style={styles.tableCell}>1 Tỷ</Text>
              <Text style={styles.tableCell}>1 Tỷ</Text>
              <Text style={styles.tableCell}>1 Tỷ</Text>
            </View>

            {/* Row 4 */}
            <View style={styles.tableRow}>
              <Text style={[styles.tableCell, styles.labelCell, { flex: 1.2 }]}>GT TK năm 10</Text>
              <Text style={styles.tableCell}>550M</Text>
              <Text style={styles.tableCell}>400M</Text>
              <Text style={styles.tableCell}>300M</Text>
            </View>

            {/* Row 5 */}
            <View style={styles.tableRow}>
              <Text style={[styles.tableCell, styles.labelCell, { flex: 1.2 }]}>GT TK năm 20</Text>
              <Text style={styles.tableCell}>820M</Text>
              <Text style={styles.tableCell}>835M</Text>
              <Text style={styles.tableCell}>850M</Text>
            </View>

            {/* Row 6 */}
            <View style={styles.tableRow}>
              <Text style={[styles.tableCell, styles.labelCell, { flex: 1.2 }]}>Hoàn lại đáo hạn</Text>
              <Text style={styles.tableCell}>820M</Text>
              <Text style={styles.tableCell}>835M</Text>
              <Text style={styles.tableCell}>850M</Text>
            </View>
          </View>
        </View>

        {/* Visual Bar chart mock */}
        <View style={styles.card}>
          <Text style={styles.cardTitle}>📊 So sánh Giá trị tài khoản năm 20 (Ước tính)</Text>
          <View style={styles.chartContainer}>
            <View style={styles.barRow}>
              <Text style={styles.barLabel}>10 năm</Text>
              <View style={styles.barWrapper}>
                <View style={[styles.barFill, { width: '80%', backgroundColor: colors.primaryFixed }]} />
                <Text style={styles.barValue}>820M</Text>
              </View>
            </View>
            <View style={styles.barRow}>
              <Text style={styles.barLabel}>15 năm</Text>
              <View style={styles.barWrapper}>
                <View style={[styles.barFill, { width: '82%', backgroundColor: colors.outline }]} />
                <Text style={styles.barValue}>835M</Text>
              </View>
            </View>
            <View style={styles.barRow}>
              <Text style={styles.barLabel}>20 năm</Text>
              <View style={styles.barWrapper}>
                <View style={[styles.barFill, { width: '85%', backgroundColor: colors.secondary }]} />
                <Text style={styles.barValue}>850M</Text>
              </View>
            </View>
          </View>
        </View>

        {/* AI suggestions card */}
        <View style={styles.aiCard}>
          <View style={styles.aiHeader}>
            <Icon name="calculate" size={20} color={colors.primary} />
            <Text style={styles.aiTitle}>Phân tích từ AI Copilot</Text>
          </View>
          <Text style={styles.aiContent}>
            Với mục tiêu "Tích lũy cho con", phương án **20 năm** mang lại giá trị hoàn lại đáo hạn tối ưu nhất (850M VNĐ) 
            nhờ thời gian tích lũy lãi kép dài hơn. Mức phí đóng 30 triệu/năm cũng giúp gia đình giữ được tỷ lệ dự phòng tài chính 
            an toàn so với thu nhập hiện tại.
          </Text>
        </View>

        {/* Actions */}
        <View style={styles.actions}>
          <TouchableOpacity style={styles.backBtn} onPress={() => navigation.goBack()}>
            <Text style={styles.backBtnText}>Quay lại</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.selectBtn} onPress={handleSelectOption}>
            <Text style={styles.selectBtnText}>Chọn & tiếp tục</Text>
          </TouchableOpacity>
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f7fa',
  },
  scrollContent: {
    padding: spacing[4],
    gap: spacing[4],
    paddingBottom: 40,
  },
  header: {
    backgroundColor: '#ffffff',
    padding: spacing[4],
    borderRadius: borderRadius.md,
    borderWidth: 1,
    borderColor: colors.outlineVariant,
    gap: spacing[1],
  },
  headerTitle: {
    fontSize: 16,
    fontWeight: fontWeights.bold,
    color: colors.primary,
  },
  headerSub: {
    fontSize: 12.5,
    color: colors.onSurfaceVariant,
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: borderRadius.md,
    padding: spacing[4],
    borderWidth: 1,
    borderColor: colors.outlineVariant,
  },
  cardTitle: {
    fontSize: 14.5,
    fontWeight: fontWeights.bold,
    color: colors.onSurface,
    marginBottom: spacing[3],
  },
  table: {
    borderWidth: 1,
    borderColor: colors.outlineVariant,
    borderRadius: borderRadius.sm,
    overflow: 'hidden',
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: colors.surface,
    borderBottomWidth: 1,
    borderBottomColor: colors.outlineVariant,
    paddingVertical: spacing[2],
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.outlineVariant,
    paddingVertical: spacing[2.5],
  },
  tableCell: {
    flex: 1,
    fontSize: 12,
    textAlign: 'center',
    color: colors.onSurface,
    alignSelf: 'center',
  },
  headerCell: {
    fontWeight: fontWeights.bold,
    color: colors.primary,
  },
  labelCell: {
    textAlign: 'left',
    paddingLeft: 8,
    fontWeight: fontWeights.semibold,
    color: colors.onSurfaceVariant,
  },
  chartContainer: {
    gap: spacing[3],
    paddingVertical: spacing[1],
  },
  barRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[2],
  },
  barLabel: {
    width: 60,
    fontSize: 12,
    color: colors.onSurfaceVariant,
  },
  barWrapper: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[2],
  },
  barFill: {
    height: 16,
    borderRadius: 8,
  },
  barValue: {
    fontSize: 12,
    fontWeight: fontWeights.bold,
    color: colors.onSurface,
  },
  aiCard: {
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: colors.secondary,
    borderRadius: borderRadius.md,
    padding: spacing[4],
    gap: spacing[2],
  },
  aiHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[2],
  },
  aiTitle: {
    fontSize: 14,
    fontWeight: fontWeights.bold,
    color: colors.secondary,
  },
  aiContent: {
    fontSize: 12.5,
    lineHeight: 18,
    color: colors.onSurface,
  },
  actions: {
    flexDirection: 'row',
    gap: spacing[3],
    marginTop: spacing[2],
  },
  backBtn: {
    flex: 1,
    borderWidth: 1,
    borderColor: colors.primary,
    borderRadius: borderRadius.DEFAULT,
    height: 48,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#ffffff',
  },
  backBtnText: {
    fontSize: 14,
    fontWeight: fontWeights.bold,
    color: colors.primary,
  },
  selectBtn: {
    flex: 2,
    backgroundColor: colors.primary,
    borderRadius: borderRadius.DEFAULT,
    height: 48,
    alignItems: 'center',
    justifyContent: 'center',
  },
  selectBtnText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: fontWeights.bold,
  },
});
